/**
 * Constants for the asset caching module
 * This file contains all constant values used throughout the asset cache system
 */

// ============================================================================
// CACHE CONFIGURATION CONSTANTS
// ============================================================================

/**
 * Default cache name for the Cache Storage API
 */
export const DEFAULT_CACHE_NAME = "asset-cache-v1";

/**
 * Default time to live for cached assets (in seconds)
 * 7 days
 */
export const DEFAULT_CACHE_TTL = 7 * 24 * 60 * 60;

/**
 * Maximum size for cacheable assets (in bytes)
 * 50MB
 */
export const MAX_CACHEABLE_SIZE = 50 * 1024 * 1024;

/**
 * Path to the cache manifest file
 */
export const CACHE_MANIFEST_PATH = "/manifest.json";

// ============================================================================
// SERVICE WORKER CONSTANTS
// ============================================================================

/**
 * Default service worker scope
 */
export const DEFAULT_SW_SCOPE = "/";

// ============================================================================
// CACHE STRATEGY MAPPINGS
// ============================================================================

/**
 * Asset types that should use a cache-first strategy
 * These are typically static assets that don't change frequently
 */
export const CACHE_FIRST_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/svg+xml",
  "font/woff",
  "font/woff2",
  "font/ttf",
  "font/otf",
];

/**
 * Asset types that should use a network-first strategy
 * These are typically large media files that benefit from fresh content
 */
export const NETWORK_FIRST_TYPES = [
  "video/mp4",
  "video/webm",
  "audio/mpeg",
  "audio/ogg",
];

/**
 * Asset types that should use a stale-while-revalidate strategy
 * These are assets that can be served stale while being updated in background
 */
export const STALE_WHILE_REVALIDATE_TYPES = [
  "application/javascript",
  "text/css",
  "application/json",
];

// ============================================================================
// FILE TYPE MAPPINGS
// ============================================================================

/**
 * File extensions to MIME type mapping
 * Used to determine the correct content type for cached assets
 */
export const FILE_EXTENSION_TO_ASSET_TYPE: Record<string, string> = {
  // Images
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  webp: "image/webp",
  svg: "image/svg+xml",
  ico: "image/x-icon",
  avif: "image/avif",
  bmp: "image/bmp",

  // Fonts
  woff: "font/woff",
  woff2: "font/woff2",
  ttf: "font/ttf",
  otf: "font/otf",
  eot: "application/vnd.ms-fontobject",

  // Videos
  mp4: "video/mp4",
  webm: "video/webm",
  ogg: "video/ogg",
  avi: "video/x-msvideo",
  mov: "video/quicktime",

  // Audio
  mp3: "audio/mpeg",
  wav: "audio/wav",
  oga: "audio/ogg",
  flac: "audio/flac",
  aac: "audio/aac",

  // Scripts and Styles
  js: "application/javascript",
  mjs: "application/javascript",
  css: "text/css",
  json: "application/json",

  // Documents
  pdf: "application/pdf",
  txt: "text/plain",
  html: "text/html",
  xml: "application/xml",
};
